import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, ButtonGroup, Dropdown } from "react-bootstrap";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { PackageSearchSelectListItem } from "src/components/PackageSearchSelectListItem";
import { FormInput, registerObject } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useListenEvent } from "go-core/hooks";
import { ReactComponent as EditSVG } from "go-core/images/svg/actions/edit.svg";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import { ReactComponent as DropdownSVG } from "go-core/images/svg/dropdown.svg";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import BarcodeScanner from "../../../../../../../../../../components/Product/BarcodeScanner/BarcodeScanner";
import { ReactComponent as MoreSVG } from "../../../../../../../../../../images/svg/documents/more.svg";
import {
	MeasureType,
	ProductApi,
	ProductStateApi,
	StockProductStateApi,
	TaxApi,
	TransferApi,
	TransferItemApi,
} from "../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../services/Api/api";
import ChangeProductModal from "../../../../components/ChangeProductModal";
import {
	areAllItemsRowsIndexesShown,
	drawProductTotalVolume,
	getProductVolumeLabel,
	handleTurnOnBarcodeScanner,
	isItemRowIndexShown,
	showMore,
	toggleAllItemsRows,
} from "../../../../utils/itemsTableUtils";

interface Props {
	form: UseFormReturn<TransferApi>;
	taxes: TaxApi[];
	fetchTransfer?: () => void;
}

const TransferFormItemsTable = ({ form, taxes, fetchTransfer }: Props) => {
	const { t } = useTranslation();
	const {
		watch,
		getValues,
		register,
		control,
		formState: { errors },
		setValue,
		clearErrors,
	} = form;
	const { fields, remove, append, replace } = useFieldArray({
		control,
		name: "items",
		keyName: "key",
	});
	const optionsRef = useRef<{ label: string }[]>([]);
	const watchedItems = watch("items");
	const [shownIndexes, setShownIndexes] = useState<number[]>([]);
	const [destinationProductStates, setDestinationProductStates] = useState<ProductStateApi[]>([]);
	const [sourceProductStates, setSourceProductStates] = useState<ProductStateApi[]>([]);
	const [showChangeProductModal, setShowChangeProductModal] = useState<number | null>(null);
	const organization = useSelector(selectOrganization);
	const watchedSourceStockId = watch("source_stock_id");
	const watchedDestinationStockId = watch("destination_stock_id");
	const watchedProductIds = watchedItems.map((item) => item.product_id).filter((id) => id);
	const itemsAsString = JSON.stringify(watchedItems);
	const { addFlash, addSuccessFlash } = useFlash();
	const [showBarcodeScanner, setShowBarcodeScanner] = useState<boolean>(false);
	const [shouldFetchProductStates, setShouldFetchProductStates] = useState<boolean>(true);
	const confirmation = useConfirmation();
	const { isMobile } = useWindowSize();

	useEffect(() => {
		onUpdateFields();
		setShouldFetchProductStates(false);
	}, []);

	const handleOpenBarcodeScanner = useCallback((e) => {
		if (e.key === "Enter") {
			if (!showBarcodeScanner) setShowBarcodeScanner(true);
		}
	}, []);

	useListenEvent({
		eventName: "keypress",
		callback: (e?: KeyboardEvent) => {
			if (!e) {
				return;
			}
			handleOpenBarcodeScanner(e);
		},
	});

	useEffect(() => {
		onUpdateFields();
	}, [fields]);

	useEffect(() => {
		setShouldFetchProductStates(true);
	}, [watchedSourceStockId, watchedDestinationStockId]);

	useEffect(() => {
		if (shouldFetchProductStates) {
			if (watch("source_stock_id")) {
				onFetchProductStates(watch("source_stock_id"), "source");
			}
			if (watch("destination_stock_id")) {
				onFetchProductStates(watch("destination_stock_id"), "destination");
			}
			setShouldFetchProductStates(false);
		}
	}, [shouldFetchProductStates]);

	const checkIfAddNewItem = () => {
		const prevItem = watchedItems[watchedItems?.length - 1];
		return prevItem?.product_id || (!isNaN(prevItem?.quantity) && prevItem.quantity !== null);
	};

	useEffect(() => {
		if (checkIfAddNewItem()) {
			append({
				position: watchedItems.length - 1,
				product: null,
				product_id: null,
				volume: null,
				quantity: null,
				unit_fifo_cost_net_money: null,
				total_fifo_cost_net_money: null,
				unit_mac_cost_net_money: null,
				total_mac_cost_net_money: null,
				comment: "",
				items: [],
			} as unknown as TransferItemApi);
			setShouldFetchProductStates(false);
		}
	}, [itemsAsString]);

	const onUpdateFields = () => {
		fields.forEach((field, index) => {
			setValue(`items.${index}.position`, index);
			registerObject(register, `items.${index}.product`, ["measure_type_unit", "name", "id", "last_price_money"]);
			setValue(`items.${index}.product`, field?.product);
			setValue(`items.${index}.product_id`, field?.product?.id);
		});
	};

	const onFetchProductStates = async (stockId: number, type: "source" | "destination") => {
		try {
			const params = {
				include: "volumes",
				stock_id: stockId,
				product_ids: watchedProductIds.join(","),
			};
			const res = await api.organization().getProductState(params);
			type === "source" ? setSourceProductStates(res) : setDestinationProductStates(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onRemoveItemsWithNoQuantity = () => {
		const indexes: number[] = [];
		watchedItems.forEach((item, index) => {
			if (!item.quantity) indexes.push(index);
		});
		remove(indexes);

		if (getValues("items").length === 0) {
			append({ position: 0 });
		}
	};

	const onRemoveDuplicatedItems = () => {
		const indexes: number[] = [];
		const uniqueItems: TransferItemApi[] = [];
		watchedItems.forEach((item, index) => {
			const existingItem = uniqueItems.find((x) => x?.product?.name === item?.product?.name);
			if (existingItem) indexes.push(index);
			else uniqueItems.push(item);
		});
		remove(indexes);
	};

	const onSortItemsAlphabetically = () => {
		const newItems = [...watchedItems];
		const sortedItems = newItems.sort((a, b) => a.product?.name.localeCompare(b?.product?.name));
		replace(sortedItems);
	};

	const onSearchProducts = async (search: string, obj: Record<string, any>, options?: Record<string, any>) => {
		return api.organization().getProductSearchSelect(
			search,
			{
				...obj,
				include: "volumes_v2",
			},
			{
				cancelToken: options?.token,
			}
		);
	};

	const onChangeProduct = async (product: ProductApi, index: number) => {
		if (!product) return;

		setValue(`items.${index}.id`, undefined);
		clearErrors(`items.${index}.volume`);
		registerObject(register, `items.${index}.product`, ["measure_type_unit", "name", "id", "last_price_money"]);
		setValue(`items.${index}.product`, product);
		setValue(`items.${index}.product_id`, product.id, { shouldDirty: true });
		setValue(`items.${index}.volume`, product?.default_volume ?? null, { shouldDirty: true });
		setShouldFetchProductStates(true);
	};
	const onParseProductOptionValue = (option: { label: string }) => {
		optionsRef.current = optionsRef.current.find((o) => o.label === option.label)
			? [...optionsRef.current]
			: [...optionsRef.current, option];
		return option.label;
	};

	const getStateForProduct = (item: ProductApi, type: "destination" | "source") => {
		if (!item) return;
		const productStates = type === "destination" ? [...destinationProductStates] : [...sourceProductStates];
		const state = productStates.find((s) => s?.product_id?.toString() === item.id.toString());

		if (state) return state.product_state;
		return { in_stock_amount: 0, commited_amount: 0, incoming_amount: 0, available_amount: 0 };
	};

	const drawVolumeInput = (item: TransferItemApi, index: number) => {
		const volumes = item?.product?.product_volumes || [];

		if (volumes.length > 0) {
			const volumesOptions = volumes.map((volume) => ({
				id: volume.volume,
				label: `${volume.volume ?? ""} ${volume.label ?? ""}`,
			}));
			const defaultValueLabel = volumes.find(
				(volume) => Number(volume.volume) === Number(watch(`items.${index}.volume`))
			)?.label;
			return (
				<FormSelectGroup
					name={`items.${index}.volume`}
					errors={errors}
					getOptionValue={(option) => option.id}
					defaultValue={{
						label: defaultValueLabel
							? `${watch(`items.${index}.volume`)} ${defaultValueLabel}`
							: watch(`items.${index}.volume`),
						id: watch(`items.${index}.volume`),
					}}
					loadOptions={() => volumesOptions}
					control={control}
					data-testid={`items.${index}.volume`}
					forceCreateOption
					onCreateOption={(option) => setValue(`items.${index}.volume`, option)}
				/>
			);
		}
		return (
			<FormNumberInput
				errors={errors}
				name={`items.${index}.volume`}
				decimalScale={5}
				suffix={
					item?.product?.measure_type ? t(`enums.products.measure_types.${item?.product?.measure_type}`) : ""
				}
				control={control}
			/>
		);
	};

	const drawProductState = (item: ProductApi, type: "destination" | "source") => {
		const state = getStateForProduct(item, type);
		if (state) {
			return `${t("common.word.in_stock")}: ${state.in_stock_amount} | 
                ${t("common.word.committed_amount").toLowerCase()}: ${state.commited_amount} |
                ${t("common.word.incoming_amount").toLowerCase()}: ${state.incoming_amount}
        `;
		}
	};

	const onConfirmChangeProduct = async (product: ProductApi) => {
		if (showChangeProductModal !== null) {
			await onChangeProduct(product, showChangeProductModal);
			setShowChangeProductModal(null);
		}
	};

	const onRemove = (index: number) => {
		remove(index);
		setShouldFetchProductStates(false);
	};

	const drawInStockBadge = (productState?: StockProductStateApi, measure_type?: MeasureType) => {
		if (!productState) return;

		let className = `ms-2 badge bg-`;
		if (productState?.in_stock_amount && productState?.in_stock_amount > 0) className += "success";
		else className += "danger";

		return (
			<span className={className}>
				{`${productState?.in_stock_amount} ${t(`enums.products.measure_types.${measure_type}`)}`}
			</span>
		);
	};

	const handleCorrection = async (item: TransferItemApi) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			await api.organization().correctProductRecipe(item.product_id);

			fetchTransfer && fetchTransfer();
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const totalMacCost = watchedItems
		.map((item) => (item?.product?.cost_price_money?.amount || 0) * (item?.quantity || 0))
		.reduce((acc, cur) => acc + cur, 0);

	return (
		<fieldset className="form-group">
			<div className="d-flex align-items-center justify-content-between">
				<h5 className={isMobile ? "mb-0" : ""}>{t("common.word.items")}</h5>
				<div>
					<Button
						onClick={() => handleTurnOnBarcodeScanner(showBarcodeScanner, setShowBarcodeScanner)}
						className="me-2"
					>
						{t("common.action.turn_on_scanner")}
					</Button>
					<Dropdown as={ButtonGroup}>
						<Dropdown.Toggle className="btn-light">
							{t("common.action.more", { ns: "lib" })}
						</Dropdown.Toggle>
						<Dropdown.Menu>
							<Dropdown.Item onClick={onRemoveItemsWithNoQuantity}>
								{t("common.action.remove_items_with_no_quantity")}
							</Dropdown.Item>
							<Dropdown.Item onClick={onRemoveDuplicatedItems}>
								{t("common.action.remove_duplicated_items")}
							</Dropdown.Item>
							<Dropdown.Item onClick={onSortItemsAlphabetically}>
								{t("common.action.sort_by_names")}
							</Dropdown.Item>
						</Dropdown.Menu>
					</Dropdown>
				</div>
			</div>
			<div className={isMobile ? "table-responsive" : ""}>
				<table className={`table table-form positions-table ${isMobile ? "vertical-top" : ""}`}>
					<thead>
						<tr>
							<th className="action" />
							<th className="w-45" style={{ minWidth: isMobile ? "200px" : "unset" }}>
								{t("common.word.product")} *
								<a
									href="javascript:"
									onClick={() =>
										toggleAllItemsRows(shownIndexes, watchedItems, setShownIndexes, fields)
									}
									className="ms-1 font-weight-normal"
								>
									{areAllItemsRowsIndexesShown(fields, shownIndexes)
										? t("common.action.collapse_all")
										: t("common.action.expand_all")}
								</a>
							</th>
							<th className="w-15" style={{ minWidth: isMobile ? "150px" : "unset" }}>
								{t("common.word.volume")} *
							</th>
							<th className="w-15" style={{ minWidth: isMobile ? "100px" : "unset" }}>
								{t("common.word.quantity")} *
							</th>
							<th className="w-15" style={{ minWidth: isMobile ? "150px" : "unset" }}>
								{t("common.word.total_volume")}
							</th>
							<th className="action" />
						</tr>
					</thead>
					<tbody>
						{watchedItems.map((item, index) => {
							return (
								<React.Fragment key={fields[index].key}>
									<tr>
										<td className="action">
											<FormInput
												errors={errors}
												type="hidden"
												register={register}
												name={`items.${index}.position`}
												defaultValue={index}
											/>
											<FormInput
												type="hidden"
												errors={errors}
												register={register}
												name={`items.${index}.id`}
												defaultValue={item.id}
											/>
										</td>
										<td>
											{!item?.product?.id ? (
												<>
													<FormSelectGroup
														name={`items.${index}.product.name`}
														errors={errors}
														onChange={(id, product) => onChangeProduct(product, index)}
														getOptionLabel={(option) => {
															return option.product_type === "PACKAGE" ? (
																<PackageSearchSelectListItem label={option.label} />
															) : (
																option.label
															);
														}}
														getOptionValue={(option) => onParseProductOptionValue(option)}
														defaultValue={{
															label: item?.product?.name,
															id: item?.product?.id,
														}}
														loadOptions={onSearchProducts}
														control={control}
														data-testid={`items.${index}.product.name`}
													/>
													<FormInput
														type="hidden"
														errors={errors}
														register={register}
														name={`items.${index}.product_id`}
														defaultValue={item.product_id}
													/>
												</>
											) : (
												<div className="d-flex align-items-center product-box">
													<DropdownSVG
														className="icon"
														id={`icon-${index}`}
														style={{
															transform: isItemRowIndexShown(shownIndexes, index)
																? "rotate(180deg)"
																: "unset",
														}}
														onClick={() => showMore(shownIndexes, setShownIndexes, index)}
													/>
													<span className="ms-1">{`${index + 1}.`}</span>
													<Link
														rel={"noreferrer"}
														target={"_blank"}
														to={`/${organization.id}/products/${item.product.id}/edit`}
														className="mx-2"
													>
														{item.product.name}
														{getProductVolumeLabel(item.product, item)}
													</Link>
													<EditSVG
														className="me-2 icon edit-icon"
														onClick={() => setShowChangeProductModal(index)}
													/>
													{drawInStockBadge(
														getStateForProduct(item.product, "source"),
														item?.product?.measure_type
													)}
													{drawInStockBadge(
														getStateForProduct(item.product, "destination"),
														item?.product?.measure_type
													)}
												</div>
											)}
										</td>
										<td>{drawVolumeInput(item, index)}</td>
										<td>
											<FormNumberInput
												errors={errors}
												name={`items.${index}.quantity`}
												suffix=""
												decimalScale={5}
												defaultValue={Number(item.quantity)}
												control={control}
											/>
										</td>
										<td colSpan={fields.length > 1 ? 1 : 2} className="nowrap">
											{drawProductTotalVolume(item, t)}
										</td>
										{fields.length > 1 && (
											<td
												className={`${
													!(
														(fields.length === 1 && index === 0) ||
														index === fields.length - 1
													)
														? "actions"
														: "action"
												}`}
											>
												<div className="d-flex align-items-center gap-2">
													{!(
														(fields.length === 1 && index === 0) ||
														index === fields.length - 1
													) && <RemoveSVG className="icon" onClick={() => onRemove(index)} />}
													{item?.product_id && (
														<Dropdown className="icon">
															<Dropdown.Toggle as={MoreSVG} />
															<Dropdown.Menu>
																{item?.product_id && (
																	<Dropdown.Item
																		onClick={() => handleCorrection(item)}
																	>
																		{t("common.action.recipe_correction")}
																	</Dropdown.Item>
																)}
															</Dropdown.Menu>
														</Dropdown>
													)}
												</div>
											</td>
										)}
									</tr>
									{isItemRowIndexShown(shownIndexes, index) &&
										getStateForProduct(item?.product, "destination") &&
										getStateForProduct(item?.product, "source") && (
											<tr>
												<td className="action" />
												<td>
													<div className="d-flex flex-column">
														<div className="d-flex flex-column">
															<small className="text-muted font-weight-bold mb-1">
																{t("modules.transfer.field.source_stock.title")}{" "}
																{watch("source_stock")?.name}:
															</small>
															{item?.product?.type !== "COMPOSITE" && (
																<small className="text-muted font-weight-bold">
																	{`${t("common.word.last_price")}: ${
																		!isNaN(item?.product?.last_price_money?.amount)
																			? FormatMoney(
																					item?.product?.last_price_money
																			  )
																			: "-"
																	}`}
																</small>
															)}
															<small className="text-muted font-weight-bold">
																{`${t("common.word.cost")}: ${FormatMoney(
																	item?.product?.cost_price_money
																)}`}
															</small>
															<small className="text-muted font-weight-bold">
																{drawProductState(item.product, "source")}
															</small>
															<small className="text-muted font-weight-bold">
																{`${t("common.word.default_volume")}: ${
																	item?.product?.default_volume
																		? item?.product?.default_volume
																		: "-"
																}`}
															</small>
														</div>
														<div className="d-flex flex-column mt-2">
															<small className="text-muted font-weight-bold mb-1">
																{t("modules.transfer.field.destination_stock.title")}{" "}
																{watch("destination_stock")?.name}:
															</small>
															{item?.product?.type !== "COMPOSITE" && (
																<small className="text-muted font-weight-bold">
																	{`${t("common.word.last_price")}: ${FormatMoney(
																		item?.product?.last_price_money
																	)}`}
																</small>
															)}
															<small className="text-muted font-weight-bold">
																{`${t("common.word.cost")}: ${FormatMoney(
																	item?.product?.cost_price_money
																)}`}
															</small>
															<small className="text-muted font-weight-bold">
																{drawProductState(item.product, "destination")}
															</small>
															<small className="text-muted font-weight-bold">
																{`${t("common.word.default_volume")}: ${
																	item?.product?.default_volume
																		? item?.product?.default_volume
																		: "-"
																}`}
															</small>
														</div>
													</div>
												</td>
												<td colSpan={4}>
													<FormInput
														errors={errors}
														register={register}
														className="comment-input"
														name={`items.${index}.comment`}
														defaultValue={item?.comment}
														placeholder={t("common.word.comment.placeholder")}
													/>
												</td>
											</tr>
										)}
								</React.Fragment>
							);
						})}
						<tr className="summary">
							{!isMobile && <td className="action" />}
							<td />
							<td colSpan={isMobile ? 0 : 2}>{t("common.word.estimated_cost")}</td>
							<td className={isMobile ? "text-start" : "text-end"}>
								{FormatMoney({ amount: totalMacCost, currency: organization?.currency || "PLN" })}
							</td>
							{!isMobile && <td className="action" />}
						</tr>
					</tbody>
				</table>
			</div>
			{showChangeProductModal !== null && (
				<ChangeProductModal
					isShown={showChangeProductModal !== null}
					onHide={() => setShowChangeProductModal(null)}
					onConfirm={onConfirmChangeProduct}
					onSearchProducts={onSearchProducts}
					index={showChangeProductModal}
					form={form}
					onParseOptionValue={onParseProductOptionValue}
				/>
			)}
			{showBarcodeScanner && (
				<BarcodeScanner
					isShown={showBarcodeScanner}
					organization={organization}
					onHide={() => setShowBarcodeScanner(false)}
					form={form}
					taxes={taxes}
				/>
			)}
		</fieldset>
	);
};

export default TransferFormItemsTable;
